import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook to initialize GSAP animations after page load.
 * Re-runs on route changes.
 */
const useGSAP = (isLoading = false) => {
  const location = useLocation();

  useEffect(() => {
    if (isLoading) return;

    let lenis = null;
    let cleanup = () => {};

    const init = async () => {
      try {
        const gsapModule = await import('gsap');
        const gsap = gsapModule.default || gsapModule.gsap;
        const stModule = await import('gsap/ScrollTrigger');
        const ScrollTrigger = stModule.default || stModule.ScrollTrigger;
        const lenisModule = await import('lenis');
        const Lenis = lenisModule.default || lenisModule.Lenis;

        gsap.registerPlugin(ScrollTrigger);

        // Lenis smooth scroll
        lenis = new Lenis({
          duration: 1.2,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          smooth: true,
        });

        function raf(time) {
          lenis.raf(time);
          requestAnimationFrame(raf);
        }
        requestAnimationFrame(raf);

        lenis.on('scroll', ScrollTrigger.update);
        gsap.ticker.add((time) => lenis.raf(time * 1000));
        gsap.ticker.lagSmoothing(0, 0);

        // Scroll to top on route change
        lenis.scrollTo(0, { immediate: true });

        // Scroll reveal
        const elements = gsap.utils.toArray('.animate-on-scroll');
        gsap.set(elements, { opacity: 0 });
        elements.forEach((el) => {
          let yOffset = 40, scaleVal = 1, xOffset = 0;
          if (el.classList.contains('zoom-in')) { yOffset = 20; scaleVal = 0.92; }
          else if (el.classList.contains('slide-right')) { yOffset = 0; xOffset = -40; }
          else if (el.classList.contains('fade-in')) { yOffset = 0; }

          gsap.fromTo(el,
            { y: yOffset, x: xOffset, scale: scaleVal, opacity: 0 },
            { y: 0, x: 0, scale: 1, opacity: 1, duration: 0.8, ease: 'power3.out',
              scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' } }
          );
        });

        // CSS-based reveal fallback/trigger (for .reveal classes)
        const revealElements = gsap.utils.toArray('.reveal, .text-reveal-trigger');
        revealElements.forEach((el) => {
          ScrollTrigger.create({
            trigger: el,
            start: 'top 85%',
            onEnter: () => el.classList.add('is-visible'),
            // onLeaveBack: () => el.classList.remove('is-visible'), // Optional: reverse on scroll up
          });
        });

        // Parallax - foreground
        gsap.utils.toArray('.parallax-element').forEach((el) => {
          const speedY = parseFloat(el.dataset.speedY || -0.2);
          gsap.to(el, {
            y: () => ScrollTrigger.maxScroll(window) * speedY * 0.5,
            ease: 'none',
            scrollTrigger: { trigger: 'body', start: 'top top', end: 'bottom top', scrub: true },
          });
        });

        // Parallax - background
        gsap.utils.toArray('.parallax-bg').forEach((bg) => {
          const speed = parseFloat(bg.dataset.speed || 0.4);
          gsap.to(bg, {
            y: () => window.innerHeight * speed,
            ease: 'none',
            scrollTrigger: { trigger: bg.parentElement || 'body', start: 'top bottom', end: 'bottom top', scrub: true },
          });
        });

        // Hero text animation
        gsap.fromTo('.hero__title-epic .word',
          { y: '110%' },
          { y: '0%', duration: 1, stagger: 0.1, ease: 'power4.out' }
        );

        // Manifesto
        const manifesto = document.querySelector('.manifesto-text');
        if (manifesto) {
          const words = manifesto.querySelectorAll('span');
          gsap.fromTo(words,
            { opacity: 0.2 },
            { opacity: 1, color: 'var(--color-primary)', stagger: 0.1, ease: 'none',
              scrollTrigger: { trigger: manifesto, start: 'top 80%', end: 'bottom 40%', scrub: 1 } }
          );
        }

        // Marquee
        const marqueeInner = document.querySelector('.marquee-inner');
        if (marqueeInner) {
          gsap.to(marqueeInner, { xPercent: -50, ease: 'none', repeat: -1, duration: 25 });
        }

        // Horizontal scroll
        const horizontalSection = document.querySelector('.horizontal-scroll-section');
        const horizontalContainer = document.querySelector('.horizontal-scroll-container');
        if (horizontalSection && horizontalContainer) {
          const getScrollAmount = () => -(horizontalContainer.scrollWidth - window.innerWidth);
          gsap.to(horizontalContainer, {
            x: getScrollAmount,
            ease: 'none',
            scrollTrigger: {
              trigger: horizontalSection,
              start: 'top top',
              end: () => `+=${getScrollAmount() * -1}`,
              pin: '.horizontal-pin-wrap',
              scrub: 1,
              invalidateOnRefresh: true,
            },
          });
        }

        // Curtain image reveal
        const curtainImg = document.querySelector('.curtain-image');
        const curtainContent = document.querySelector('.curtain-content');
        if (curtainImg && curtainContent) {
          const tl = gsap.timeline({
            scrollTrigger: { trigger: '.image-curtain-section', start: 'top top', end: 'bottom bottom', scrub: true },
          });
          tl.to(curtainImg, { width: '100vw', height: '100vh', borderRadius: '0px', ease: 'power1.inOut' })
            .to(curtainContent, { opacity: 1, y: -20, ease: 'power2.out' }, '-=0.2');
        }

        // Footer curtain reveal
        const main = document.getElementById('main-content');
        const footer = document.querySelector('.footer');
        if (main && footer) {
          const updateFooter = () => {
            if (window.matchMedia('(max-width: 768px)').matches) {
              main.style.marginBottom = '0';
              footer.style.position = 'static';
              footer.style.bottom = '';
              footer.style.left = '';
              footer.style.width = '100%';
              footer.style.zIndex = '';
              return;
            }
            main.style.marginBottom = footer.offsetHeight + 'px';
            footer.style.position = 'fixed';
            footer.style.bottom = '0';
            footer.style.left = '0';
            footer.style.width = '100%';
            footer.style.zIndex = '1';
          };
          window.addEventListener('resize', updateFooter);
          setTimeout(updateFooter, 500);
          cleanup = () => window.removeEventListener('resize', updateFooter);
        }

        ScrollTrigger.refresh();
      } catch (e) {
        // GSAP not available, use fallback
        initFallback();
      }
    };

    init();

    return () => {
      cleanup();
      if (lenis) lenis.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isLoading]);
};

function initFallback() {
  // Scroll reveal fallback
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll, .reveal, .text-reveal-trigger').forEach((el) => observer.observe(el));
  }

  // Hero text visibility
  document.querySelectorAll('.hero__title-epic .word').forEach((word) => {
    word.style.transform = 'translateY(0)';
    word.style.transition = 'transform 0.6s ease-out';
  });
}

export default useGSAP;
